/**
 * Phase 140A — Broadcaster Score Panel.
 *
 * Lives in the operator cockpit (LivexStudio) right column. Drives all
 * three broadcaster controls:
 *
 *   • Team identity (names, colors, optional logo URLs)  → PUT /scoreboard
 *   • Sport-specific score increments                    → POST /scoreboard/score
 *   • Match timer + period switcher                      → POST /scoreboard/timer
 *
 * The sport template is fetched once from /api/livex/sport-templates and
 * drives the rendered button labels. Sport changes reset the underlying
 * score shape (handled server-side).
 *
 * Optimistic UI: every action updates the local `sb` immediately, then
 * reconciles with the server response. On error we revert + toast.
 */
import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Play, Pause, RotateCcw, Undo2 } from 'lucide-react';

function _zeroScore(shape) {
  if (shape === 'cricket') return { runs: 0, wickets: 0, overs: 0 };
  if (shape === 'set_point') return { points: 0, sets: 0 };
  return 0;
}

function _renderScore(score, shape) {
  if (shape === 'cricket') {
    const s = score || {};
    return `${s.runs ?? 0}/${s.wickets ?? 0} (${s.overs ?? 0})`;
  }
  if (shape === 'set_point') {
    const s = score || {};
    return `${s.points ?? 0} · ${s.sets ?? 0} sets`;
  }
  return String(score ?? 0);
}

function useLocalTimer(timer) {
  // Mirror of ScoreboardOverlay's useLiveTimer — keeps the broadcaster's
  // timer display in sync without making the hook itself impure (we update
  // state inside the interval rather than reading Date.now() during render).
  const [display, setDisplay] = useState('00:00');
  useEffect(() => {
    function compute() {
      if (!timer) { setDisplay('00:00'); return; }
      let elapsed = Number(timer.offset_ms || 0);
      if (timer.running && timer.started_at_ms) elapsed += Date.now() - Number(timer.started_at_ms);
      const t = Math.max(0, Math.floor(elapsed / 1000));
      setDisplay(`${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`);
    }
    compute();
    if (!timer?.running) return undefined;
    const id = setInterval(compute, 1000);
    return () => clearInterval(id);
  }, [timer]);
  return display;
}

export default function BroadcasterScorePanel({ streamId, initialScoreboard }) {
  const [sb, setSb] = useState(initialScoreboard);
  const [templates, setTemplates] = useState(null);
  const elapsed = useLocalTimer(sb?.timer);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/livex/sport-templates');
        setTemplates(r.data.templates);
      } catch (_e) {
        // Soft-fail; we render generic buttons.
      }
    })();
  }, []);

  const tmpl = templates && sb?.sport ? templates[sb.sport] || templates.generic : null;

  const _applyServerResponse = (data) => {
    if (data?.scoreboard) setSb(data.scoreboard);
  };

  const _patchConfig = useCallback(async (patch) => {
    if (!streamId) return;
    try {
      const r = await api.put(`/livex/streams/${streamId}/scoreboard`, patch);
      _applyServerResponse(r.data);
    } catch (e) {
      toast.error('Could not save scoreboard');
    }
  }, [streamId]);

  const _adjustScore = useCallback(async (side, delta, field, resetField) => {
    if (!streamId) return;
    try {
      const r = await api.post(`/livex/streams/${streamId}/scoreboard/score`, {
        side, delta, field: field || null, reset_field: resetField || null,
      });
      _applyServerResponse(r.data);
    } catch (e) {
      toast.error('Score update failed');
    }
  }, [streamId]);

  const _controlTimer = useCallback(async (action, period) => {
    if (!streamId) return;
    try {
      const r = await api.post(`/livex/streams/${streamId}/scoreboard/timer`, {
        action, period: period ?? null,
      });
      _applyServerResponse(r.data);
    } catch (e) {
      toast.error('Timer update failed');
    }
  }, [streamId]);

  if (!sb) {
    return (
      <div className="p-5 bg-[#07111F] border-2 border-[#22314F] rounded-xl" data-testid="broadcaster-score-panel-empty">
        <div className="livex-label" style={{ marginBottom: '8px' }}>Scoreboard</div>
        <div className="text-xs text-white/60">Loading match controls…</div>
      </div>
    );
  }

  const sportOptions = templates ? Object.keys(templates) : [sb.sport || 'generic'];

  return (
    <div className="p-5 bg-[#07111F] border-2 border-[#22314F] rounded-xl space-y-4" data-testid="broadcaster-score-panel">
      <div className="flex items-center justify-between">
        <div className="livex-label" style={{ marginBottom: 0 }}>Scoreboard</div>
        <label className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-white/70 cursor-pointer">
          <input
            type="checkbox"
            checked={sb.enabled !== false}
            onChange={(e) => _patchConfig({ enabled: e.target.checked })}
            data-testid="scoreboard-enabled-toggle"
          />
          Show on stream
        </label>
      </div>

      {/* Team identity */}
      <div className="grid grid-cols-2 gap-2">
        {['home', 'away'].map((side) => (
          <div key={side} className="rounded-lg bg-[#0c1a2e] border border-[#22314F] p-3 space-y-2">
            <div className="text-[10px] uppercase tracking-wider font-bold text-white/60">{side === 'home' ? 'Home' : 'Away'}</div>
            <input
              type="text"
              defaultValue={sb[side]?.name || ''}
              onBlur={(e) => _patchConfig({ [`${side}_name`]: e.target.value })}
              placeholder={side === 'home' ? 'Home team' : 'Away team'}
              data-testid={`scoreboard-${side}-name`}
              className="w-full px-2 py-1.5 text-xs font-bold bg-[#070d16] border border-[#1f2a44] rounded text-white placeholder-white/30"
            />
            <input
              type="color"
              defaultValue={sb[side]?.color || (side === 'home' ? '#0f766e' : '#dc2626')}
              onChange={(e) => _patchConfig({ [`${side}_color`]: e.target.value })}
              data-testid={`scoreboard-${side}-color`}
              className="w-full h-7 rounded cursor-pointer bg-transparent"
            />
            <div className="text-2xl font-black text-white tabular-nums text-center" data-testid={`scoreboard-${side}-score`}>
              {_renderScore(sb[side]?.score, sb.score_shape)}
            </div>
            {/* Sport-aware score buttons */}
            <div className="flex flex-wrap gap-1 justify-center">
              {(tmpl?.increments || [{ label: '+1', value: 1 }]).map((inc, i) => (
                <button
                  key={`${side}-${i}`}
                  onClick={() => _adjustScore(side, inc.value, inc.field, inc.reset_field)}
                  data-testid={`scoreboard-${side}-inc-${i}`}
                  className="px-2 py-1 text-[10px] font-black uppercase tracking-wider bg-emerald-700 hover:bg-emerald-600 text-white rounded transition-colors"
                >
                  {inc.label}
                </button>
              ))}
              <button
                onClick={() => _adjustScore(side, -1, tmpl?.increments?.[0]?.field || null)}
                title="Undo last point"
                data-testid={`scoreboard-${side}-undo`}
                className="px-2 py-1 text-[10px] font-bold bg-slate-700 hover:bg-slate-600 text-white/90 rounded transition-colors"
              >
                <Undo2 className="h-3 w-3 inline" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Sport + Period + Timer */}
      <div className="rounded-lg bg-[#0c1a2e] border border-[#22314F] p-3 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-white/60 mb-1">Sport</div>
            <select
              value={sb.sport || 'generic'}
              onChange={(e) => _patchConfig({ sport: e.target.value })}
              data-testid="scoreboard-sport-select"
              className="w-full px-2 py-1.5 text-xs font-bold bg-[#070d16] border border-[#1f2a44] rounded text-white"
            >
              {sportOptions.map((s) => (
                <option key={s} value={s}>{templates?.[s]?.label || s}</option>
              ))}
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-white/60 mb-1">Period</div>
            <select
              value={sb.period || ''}
              onChange={(e) => _controlTimer('start', e.target.value)}
              data-testid="scoreboard-period-select"
              disabled={!tmpl?.periods?.length}
              className="w-full px-2 py-1.5 text-xs font-bold bg-[#070d16] border border-[#1f2a44] rounded text-white disabled:opacity-40"
            >
              <option value="">—</option>
              {(tmpl?.periods || []).map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
        </div>

        {sb.timer?.direction !== 'off' && (
          <div className="flex items-center justify-between bg-[#070d16] border border-[#1f2a44] rounded px-3 py-2">
            <span className="text-2xl font-mono font-black text-white tabular-nums" data-testid="scoreboard-timer-display">{elapsed}</span>
            <div className="flex gap-1">
              {sb.timer?.running ? (
                <button
                  onClick={() => _controlTimer('pause')}
                  data-testid="scoreboard-timer-pause"
                  className="px-2 py-1 text-[10px] font-black bg-amber-600 hover:bg-amber-500 text-white rounded"
                >
                  <Pause className="h-3 w-3 inline" />
                </button>
              ) : (
                <button
                  onClick={() => _controlTimer('start')}
                  data-testid="scoreboard-timer-start"
                  className="px-2 py-1 text-[10px] font-black bg-emerald-700 hover:bg-emerald-600 text-white rounded"
                >
                  <Play className="h-3 w-3 inline" />
                </button>
              )}
              <button
                onClick={() => _controlTimer('reset')}
                data-testid="scoreboard-timer-reset"
                className="px-2 py-1 text-[10px] font-bold bg-slate-700 hover:bg-slate-600 text-white/90 rounded"
              >
                <RotateCcw className="h-3 w-3 inline" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
